import React, { useEffect, useMemo, useState } from 'react';
import {
  Sparkles, Plus, Trash2, GripVertical, Save, RotateCcw, RefreshCw,
} from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

/**
 * Admin-facing editor for the "Daily Spin" prize pool.
 *
 * Renders a table of rewards (drag-to-reorder), inline edit for
 * type / value / label / probability, and a live probability-sum bar so
 * admins always know how close to 100% they are before saving. Backend
 * normalises the probabilities on write, so any positive sum is
 * mathematically valid — but we surface the raw sum so admins spot
 * lopsided rolls (e.g. a jackpot at 95%).
 *
 * Backend contract (see /api/admin/settings/spin-rewards):
 *   GET  → { rewards: [{type, value, label, probability}], defaults, using_defaults }
 *   PUT  → body { rewards: [...] }  (probabilities auto-normalised server-side)
 */
const TYPES = [
  { value: 'points',     label: 'Points'      },
  { value: 'bonus',      label: 'Cash Bonus'  },
  { value: 'badge',      label: 'Badge'       },
  { value: 'multiplier', label: 'Multiplier'  },
];

function newRow() {
  return { type: 'bonus', value: 1, label: '$1 Bonus', probability: 0.05, _key: Math.random().toString(36).slice(2) };
}

export default function SpinRewardsStudio() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/settings/spin-rewards');
      setRows((res.rewards || []).map((r) => ({ ...r, _key: Math.random().toString(36).slice(2) })));
      setDefaults(res.defaults || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load spin rewards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalProbability = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.probability) || 0), 0),
    [rows],
  );

  const update = (i, patch) => {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const remove = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const add = () => setRows((rs) => [...rs, newRow()]);

  const onDragStart = (i) => setDragIndex(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    setRows((rs) => {
      const copy = [...rs];
      const [item] = copy.splice(dragIndex, 1);
      copy.splice(i, 0, item);
      setDragIndex(i);
      return copy;
    });
  };
  const onDragEnd = () => setDragIndex(null);

  const save = async () => {
    if (rows.length === 0) {
      toast.error('Add at least one reward before saving');
      return;
    }
    if (totalProbability <= 0) {
      toast.error('At least one reward needs a probability greater than 0');
      return;
    }
    setSaving(true);
    try {
      const payload = rows.map(({ _key, ...r }) => ({
        type: r.type,
        value: Number(r.value) || 0,
        label: r.label || `${r.type} × ${r.value}`,
        probability: Number(r.probability) || 0,
      }));
      const res = await apiCall('/api/admin/settings/spin-rewards', {
        method: 'PUT',
        body: JSON.stringify({ rewards: payload }),
      });
      setRows((res.rewards || []).map((r) => ({ ...r, _key: Math.random().toString(36).slice(2) })));
      toast.success('Spin rewards saved and normalised');
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (!defaults?.length) return;
    setRows(defaults.map((r) => ({ ...r, _key: Math.random().toString(36).slice(2) })));
    toast.info('Loaded platform defaults. Click Save to persist.');
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid="spin-rewards-studio">
      <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gradient-to-br from-fuchsia-500 to-purple-500 rounded-xl">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Spin Rewards Studio</h2>
            <p className="text-sm text-slate-500 max-w-xl">
              Configure the Daily Spin prize pool. Reorder rows by dragging the handle. Probabilities are
              normalised to 100% on save.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid="spin-rewards-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={resetDefaults}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid="spin-rewards-reset"
          >
            <RotateCcw className="w-4 h-4" /> Load defaults
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50"
            data-testid="spin-rewards-save"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save rewards'}
          </button>
        </div>
      </div>

      {/* Live probability meter */}
      <div className="px-6 pt-4">
        <div className="flex items-center justify-between text-sm text-slate-600 mb-1">
          <span>Probability sum</span>
          <span className={totalProbability > 1 ? 'text-red-600 font-semibold' : 'text-slate-800 font-semibold'}
                data-testid="spin-rewards-prob-sum">
            {(totalProbability * 100).toFixed(1)}%
          </span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${totalProbability > 1 ? 'bg-red-500' : totalProbability < 0.99 ? 'bg-amber-500' : 'bg-emerald-500'}`}
            style={{ width: `${Math.min(100, totalProbability * 100)}%` }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {totalProbability > 1 && 'Sum exceeds 100% — probabilities will be normalised on save.'}
          {totalProbability > 0 && totalProbability < 0.99 && 'Sum below 100% — remaining share is distributed proportionally on save.'}
          {totalProbability >= 0.99 && totalProbability <= 1 && 'Ready to save — probabilities align to 100%.'}
        </p>
      </div>

      {/* Table */}
      <div className="p-6 overflow-x-auto">
        {loading ? (
          <div className="text-center text-slate-500 py-10">Loading rewards…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 uppercase text-xs tracking-wider">
                <th className="w-8"></th>
                <th className="pb-2">#</th>
                <th className="pb-2">Type</th>
                <th className="pb-2">Value</th>
                <th className="pb-2">Label (shown to worker)</th>
                <th className="pb-2 w-40">Probability</th>
                <th className="pb-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={r._key}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={(e) => onDragOver(e, i)}
                  onDragEnd={onDragEnd}
                  className={`border-t border-slate-100 ${dragIndex === i ? 'bg-fuchsia-50/50' : ''}`}
                  data-testid={`spin-rewards-row-${i}`}
                >
                  <td className="py-2 pr-2 text-slate-400 cursor-grab active:cursor-grabbing">
                    <GripVertical className="w-4 h-4" />
                  </td>
                  <td className="py-2 pr-3 text-slate-400 font-mono text-xs">{i + 1}</td>
                  <td className="py-2 pr-3">
                    <select
                      value={r.type}
                      onChange={(e) => update(i, { type: e.target.value })}
                      className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
                      data-testid={`spin-rewards-row-${i}-type`}
                    >
                      {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="number"
                      step="0.01"
                      value={r.value}
                      onChange={(e) => update(i, { value: e.target.value })}
                      className="w-24 border border-slate-200 rounded-lg px-2 py-1.5"
                      data-testid={`spin-rewards-row-${i}-value`}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      value={r.label}
                      onChange={(e) => update(i, { label: e.target.value })}
                      className="w-full min-w-[10rem] border border-slate-200 rounded-lg px-2 py-1.5"
                      data-testid={`spin-rewards-row-${i}-label`}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={r.probability}
                        onChange={(e) => update(i, { probability: e.target.value })}
                        className="w-20 border border-slate-200 rounded-lg px-2 py-1.5"
                        data-testid={`spin-rewards-row-${i}-prob`}
                      />
                      <span className="text-xs text-slate-500">
                        {((parseFloat(r.probability) || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => remove(i)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Delete reward"
                      data-testid={`spin-rewards-row-${i}-remove`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-slate-500">No rewards yet — add one below.</td></tr>
              )}
            </tbody>
          </table>
        )}

        <button
          onClick={add}
          className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed border-slate-300 text-slate-600 hover:bg-slate-50"
          data-testid="spin-rewards-add"
        >
          <Plus className="w-4 h-4" /> Add reward
        </button>
      </div>
    </div>
  );
}
